/* eslint-disable no-unused-vars */
import { ReactNode, useEffect, useRef, useState } from "react";
import parse from "html-react-parser";
import { commonHtmlParserOptions } from "@utils/html-parser";
import { twMerge } from "tailwind-merge";
import classNames from "classnames";
import { IconButton } from "@shared/components/icon-button";
import AltArrowDown from "@svg/new-design/alt-arrow-down";
import { ICON_COLOR, ICON_SIZE } from "@shared/components/icon-button";
import { processMathTags } from "@modules/katex/katexFormatter";

interface AccordionProps {
  title: string | ReactNode;
  open?: boolean;
  hasResult?: boolean;
  className?: ((isOpen?: boolean) => string) | string;
  titleClassName?: ((isOpen?: boolean) => string) | string;
  contentClassName?: ((isOpen?: boolean) => string) | string;
  openAccordion?: (isOpen: boolean) => void;
  iconComponent?: (isOpen?: boolean) => React.ReactNode;
  titleIsHtml?: boolean;
  isLocked?: boolean;
  lockClickHandler?: () => void;
  children?: ReactNode;
}

const Accordion: React.FC<AccordionProps> = ({
  titleIsHtml,
  title,
  open,
  children,
  className = "",
  titleClassName = "",
  contentClassName = "",
  iconComponent,
  openAccordion = () => {},
  isLocked,
  lockClickHandler,
}) => {
  const [isOpen, setOpen] = useState<boolean>(open || false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setOpen(Boolean(open));
  }, [open]);

  useEffect(() => {
    const el = contentRef?.current;
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries && entries.length > 0) {
        const { height } = entries[0].contentRect;
        setContentHeight(height);
      }
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      if (el) resizeObserver.unobserve(el);
      resizeObserver.disconnect();
    };
  }, []);

  const toggleAccordion = () => {
    if (isLocked) return lockClickHandler?.();
    openAccordion(!isOpen);
    setOpen(!isOpen);
  };

  const renderIconComponent = iconComponent ? (
    iconComponent(isOpen)
  ) : (
    <IconButton
      aria-label="باز و بستن محتوا"
      icon={<AltArrowDown />}
      iconClassName={classNames(
        "transition-transform duration-300 ease-linear",
        { "rotate-180": isOpen }
      )}
      size={ICON_SIZE.MEDIUM}
      color={ICON_COLOR.DEFAULT}
    />
  );
  const wrapperClassNames =
    typeof className === "string" ? className : className(isOpen);
  const titleClassNames =
    typeof titleClassName === "string"
      ? titleClassName
      : titleClassName(isOpen);
  const contentClassNames =
    typeof contentClassName === "string"
      ? contentClassName
      : contentClassName(isOpen);

  return (
    <div className="w-full">
      <div
        onClick={toggleAccordion}
        className={twMerge(
          "flex justify-between items-center box-border h-[59px]",
          isLocked && !lockClickHandler ? "cursor-auto" : "cursor-pointer",
          wrapperClassNames
        )}
      >
        <div
          className={twMerge(
            "font-bold flex-shrink overflow-auto break-words",
            titleClassNames
          )}
        >
          {titleIsHtml && typeof title === "string"
            ? parse(processMathTags(title || ""), commonHtmlParserOptions())
            : title}
        </div>
        {renderIconComponent}
      </div>
      <div
        className={twMerge(
          "w-full overflow-hidden transition-max-height duration-300 ease-linear",
          contentClassNames
        )}
        style={{ maxHeight: isOpen ? contentHeight : 0 }}
      >
        <div ref={contentRef} className="w-full flex flex-col">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Accordion;
